import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Users, ArrowLeft, Zap, TrendingUp, Activity,
  MapPin, Eye, CheckCircle, AlertCircle, BarChart3, Euro, Percent
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [energyStats, setEnergyStats] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProducer, setSelectedProducer] = useState(null);
  const [producerDetail, setProducerDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (user && user.role !== 'pro' && user.role !== 'admin') {
      toast.error('Accès réservé aux professionnels');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [refRes, statsRes, commRes, commissionRes] = await Promise.all([
        axios.get(`${API_URL}/api/pro/referrals`, { headers }),
        axios.get(`${API_URL}/api/pro/energy-stats`, { headers }),
        axios.get(`${API_URL}/api/pro/communities`, { headers }),
        axios.get(`${API_URL}/api/pro/commissions`, { headers }).catch(() => ({ data: null })),
      ]);
      setReferrals(refRes.data.referrals || []);
      setEnergyStats(statsRes.data);
      setCommunities(commRes.data.communities || []);
      setCommissions(commissionRes.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducerDetail = async (producerId) => {
    setDetailLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/pro/producer/${producerId}/detail`, { headers });
      setProducerDetail(res.data);
      setSelectedProducer(producerId);
      setActiveTab('producer-detail');
    } catch (error) {
      toast.error('Erreur de chargement du detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const producers = referrals.filter(r => r.user_type === 'producer');
  const consumers = referrals.filter(r => r.user_type === 'consumer');
  const completeProfiles = referrals.filter(r => r.profile_complete).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8faf9]">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground" data-testid="pro-dashboard-title">
                Tableau de bord Demarcheur
              </h1>
              <p className="text-sm text-muted-foreground">Parrainage et commissions</p>
            </div>
          </div>
          <Badge className="bg-purple-100 text-purple-700">
            <Eye className="w-3 h-3 mr-1" /> Mode pro
          </Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-white" data-testid="kpi-referrals">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{referrals.length}</p>
                  <p className="text-xs text-muted-foreground">Filleuls</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white" data-testid="kpi-energy">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{commissions?.total_energy_kwh?.toFixed(0) || 0}</p>
                  <p className="text-xs text-muted-foreground">kWh echanges</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white" data-testid="kpi-margin">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{commissions?.total_margin_eur?.toFixed(2) || '0.00'}€</p>
                  <p className="text-xs text-muted-foreground">Marge generee</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200" data-testid="kpi-commission">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{commissions?.total_commission_eur?.toFixed(2) || '0.00'}€</p>
                  <p className="text-xs text-green-600 font-medium">Commission (50%)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white" data-testid="kpi-communities">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{communities.length}</p>
                  <p className="text-xs text-muted-foreground">Communautes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-0 overflow-x-auto">
          {[
            { key: 'overview', label: 'Vue d\'ensemble' },
            { key: 'commissions', label: 'Commissions' },
            { key: 'producers', label: 'Producteurs' },
            { key: 'referrals', label: 'Filleuls' },
            ...(selectedProducer ? [{ key: 'producer-detail', label: `Detail producteur` }] : []),
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === t.key ? 'border-green-600 text-green-700' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`pro-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Monthly Stats */}
            {commissions?.monthly_commissions?.length > 0 && (
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Commissions mensuelles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Mois</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Energie (kWh)</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Marge (€)</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Commission (€)</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Transactions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commissions.monthly_commissions.map((m, i) => (
                          <tr key={i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3 font-medium">{m.month}</td>
                            <td className="py-2 px-3 text-right">{m.energy_kwh.toFixed(1)}</td>
                            <td className="py-2 px-3 text-right">{m.margin_eur.toFixed(2)}€</td>
                            <td className="py-2 px-3 text-right font-bold text-green-600">{m.commission_eur.toFixed(2)}€</td>
                            <td className="py-2 px-3 text-right">{m.tx_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Communities */}
              <Card className="bg-white" data-testid="pro-communities">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    Mes communautes ({communities.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {communities.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                      <div>
                        <p className="font-medium text-sm">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.city}, {c.department}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{c.member_count} membres</p>
                        <p className="text-xs text-muted-foreground">{c.referral_count} recrutes</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Profile Status */}
              <Card className="bg-white" data-testid="pro-profile-status">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    Etat des profils
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-green-50 rounded-xl">
                      <p className="text-lg font-bold text-green-700">{producers.length}</p>
                      <p className="text-xs text-green-600">Producteurs</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-xl">
                      <p className="text-lg font-bold text-blue-700">{consumers.length}</p>
                      <p className="text-xs text-blue-600">Consommateurs</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-xl">
                      <p className="text-lg font-bold text-amber-700">{completeProfiles}/{referrals.length}</p>
                      <p className="text-xs text-amber-600">Profils complets</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && (
          <div className="space-y-6">
            {/* Commission summary */}
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Percent className="w-6 h-6 text-green-700" />
                  <h3 className="text-lg font-bold text-green-800">Recapitulatif des commissions</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/70 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-green-700">{commissions?.total_commission_eur?.toFixed(2) || '0.00'}€</p>
                    <p className="text-xs text-green-600">Total commissions</p>
                  </div>
                  <div className="bg-white/70 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-amber-700">{commissions?.total_margin_eur?.toFixed(2) || '0.00'}€</p>
                    <p className="text-xs text-amber-600">Marge totale</p>
                  </div>
                  <div className="bg-white/70 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold">{(commissions?.commission_rate * 100)?.toFixed(0) || 50}%</p>
                    <p className="text-xs text-muted-foreground">Taux de commission</p>
                  </div>
                  <div className="bg-white/70 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold">{commissions?.per_producer?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Producteurs actifs</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Per producer breakdown */}
            {commissions?.per_producer?.length > 0 && (
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    Commission par producteur
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground">Producteur</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Energie (kWh)</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Marge (€)</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Commission (€)</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-muted-foreground">Transactions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commissions.per_producer.map((p, i) => (
                          <tr key={i} className="border-b hover:bg-muted/30" data-testid={`commission-producer-${i}`}>
                            <td className="py-2 px-3">
                              <p className="font-medium text-sm">{p.name}</p>
                              <p className="text-xs text-muted-foreground">{p.email}</p>
                            </td>
                            <td className="py-2 px-3 text-right">{p.energy_kwh.toFixed(1)}</td>
                            <td className="py-2 px-3 text-right">{p.margin_eur.toFixed(2)}€</td>
                            <td className="py-2 px-3 text-right font-bold text-green-600">{p.commission_eur.toFixed(2)}€</td>
                            <td className="py-2 px-3 text-right">{p.tx_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <Card className="bg-white" data-testid="pro-referrals-list">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Utilisateurs recrutes ({referrals.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-3 text-sm font-medium text-muted-foreground">Nom</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-muted-foreground hidden sm:table-cell">Email</th>
                      <th className="text-center py-3 px-3 text-sm font-medium text-muted-foreground">Type</th>
                      <th className="text-center py-3 px-3 text-sm font-medium text-muted-foreground">Linky</th>
                      <th className="text-center py-3 px-3 text-sm font-medium text-muted-foreground">RIB</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-muted-foreground hidden md:table-cell">Communautes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((r, i) => (
                      <tr key={i} className="border-b hover:bg-muted/30">
                        <td className="py-3 px-3 font-medium text-sm">{r.name}</td>
                        <td className="py-3 px-3 text-muted-foreground text-sm hidden sm:table-cell">{r.email}</td>
                        <td className="py-3 px-3 text-center">
                          <Badge variant={r.user_type === 'producer' ? 'default' : 'outline'} className="text-xs">
                            {r.user_type === 'producer' ? 'Producteur' : 'Consommateur'}
                          </Badge>
                        </td>
                        <td className="py-3 px-3 text-center">
                          {r.has_linky ? (
                            <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-amber-400 mx-auto" />
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          {r.has_rib ? (
                            <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-amber-400 mx-auto" />
                          )}
                        </td>
                        <td className="py-3 px-3 text-sm hidden md:table-cell">
                          {r.communities?.map(c => c.name).join(', ') || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Producers Tab - Vue d'ensemble de tous les producteurs */}
        {activeTab === 'producers' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-green-600" />
                  Mes producteurs ({producers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {commissions?.per_producer?.length > 0 ? (
                  <div className="space-y-3">
                    {commissions.per_producer.map((p, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-4 bg-muted/30 rounded-xl hover:bg-muted/50 cursor-pointer transition-colors border border-transparent hover:border-green-200"
                        onClick={() => fetchProducerDetail(p.producer_id)}
                        data-testid={`producer-card-${i}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                            <span className="text-sm font-bold text-green-700">{p.name?.charAt(0) || '?'}</span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{p.name}</p>
                            <p className="text-xs text-muted-foreground">{p.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 text-right">
                          <div className="hidden sm:block">
                            <p className="text-sm font-medium">{p.energy_kwh.toFixed(0)} kWh</p>
                            <p className="text-xs text-muted-foreground">produit</p>
                          </div>
                          <div className="hidden md:block">
                            <p className="text-sm font-medium text-green-600">{(p.energy_kwh * 0.10).toFixed(2)}€</p>
                            <p className="text-xs text-muted-foreground">revenu prod.</p>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-green-700">{p.commission_eur.toFixed(2)}€</p>
                            <p className="text-xs text-green-600">ma commission</p>
                          </div>
                          <Eye className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Aucun producteur avec des transactions</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Producer Detail Tab */}
        {activeTab === 'producer-detail' && producerDetail && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setActiveTab('producers')} data-testid="back-to-producers">
                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
              </Button>
              <div>
                <h3 className="text-lg font-semibold">{producerDetail.producer.name}</h3>
                <p className="text-sm text-muted-foreground">{producerDetail.producer.email}</p>
              </div>
            </div>

            {/* KPIs du producteur */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="bg-white">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold">{producerDetail.summary.total_energy_kwh.toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">kWh produits</p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-blue-600">{producerDetail.summary.total_revenue_eur.toFixed(2)}€</p>
                  <p className="text-xs text-muted-foreground">Revenu producteur</p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-amber-600">{producerDetail.summary.total_margin_eur.toFixed(2)}€</p>
                  <p className="text-xs text-muted-foreground">Marge generee</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-green-700">{producerDetail.summary.total_commission_eur.toFixed(2)}€</p>
                  <p className="text-xs text-green-600 font-medium">Ma commission (50%)</p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold">{producerDetail.summary.total_transactions}</p>
                  <p className="text-xs text-muted-foreground">Transactions</p>
                </CardContent>
              </Card>
            </div>

            {/* Historique mensuel */}
            {producerDetail.monthly_breakdown.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> Production et ventes mensuelles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">Mois</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Production (kWh)</th>
                          <th className="text-right py-2 px-3 font-medium text-blue-600">Revenu prod. (€)</th>
                          <th className="text-right py-2 px-3 font-medium text-amber-600">Marge (€)</th>
                          <th className="text-right py-2 px-3 font-medium text-green-600">Commission (€)</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Tx</th>
                        </tr>
                      </thead>
                      <tbody>
                        {producerDetail.monthly_breakdown.map((m, i) => (
                          <tr key={i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3 font-medium">{m.month}</td>
                            <td className="py-2 px-3 text-right">{m.energy_kwh.toFixed(1)}</td>
                            <td className="py-2 px-3 text-right text-blue-600">{m.revenue_eur.toFixed(2)}€</td>
                            <td className="py-2 px-3 text-right text-amber-600">{m.margin_eur.toFixed(2)}€</td>
                            <td className="py-2 px-3 text-right font-bold text-green-600">{m.commission_eur.toFixed(2)}€</td>
                            <td className="py-2 px-3 text-right">{m.tx_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Par communaute */}
              {producerDetail.by_community.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Par communaute
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {producerDetail.by_community.map((c, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{c.name}</p>
                            <p className="text-xs text-muted-foreground">{c.tx_count} transactions</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">{c.energy_kwh.toFixed(0)} kWh</p>
                            <p className="text-xs text-green-600">{c.revenue_eur.toFixed(2)}€ revenu</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top acheteurs */}
              {producerDetail.top_consumers.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="w-4 h-4" /> Principaux acheteurs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {producerDetail.top_consumers.map((c, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{c.name}</p>
                            <p className="text-xs text-muted-foreground">{c.tx_count} achats</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">{c.energy_kwh.toFixed(0)} kWh</p>
                            <p className="text-xs text-blue-600">{c.amount_paid_eur.toFixed(2)}€ paye</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Dernieres transactions */}
            {producerDetail.recent_transactions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-4 h-4" /> Dernieres transactions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">Date</th>
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">Acheteur</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">kWh</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Prix/kWh</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {producerDetail.recent_transactions.map((t, i) => (
                          <tr key={i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3">{t.date?.substring(0, 10)}</td>
                            <td className="py-2 px-3">{t.consumer_name}</td>
                            <td className="py-2 px-3 text-right">{t.energy_kwh}</td>
                            <td className="py-2 px-3 text-right">{t.price_per_kwh?.toFixed(3)}€</td>
                            <td className="py-2 px-3 text-right font-medium">{t.total_price.toFixed(2)}€</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
